/**
 * DestructiveWarningModal - Confirmation dialog for proposals with destructive actions.
 *
 * Shows a warning when approving proposals that contain delete or merge operations.
 * Requires explicit user acknowledgment before proceeding.
 */

import { GitMerge, Trash03 } from '@untitledui/icons';
import { useCallback, useEffect, useState } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';
import { ConfirmationDialog } from '@/lib/base/overlays';
import {
  getObjectDeletedTitle,
  getObjectMergedTitle,
} from '@/lib/proposals/registry/registry.helpers';

import type { ProposedAction } from '../../lib/proposals/core/types';
import {
  isObjectDeletedPayload,
  isObjectMergedPayload,
} from '../../lib/proposals/core/types';

export interface DestructiveWarningModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Array of destructive actions to display */
  destructiveActions: ProposedAction[];
  /** Whether an operation is in progress */
  isLoading?: boolean;
  /** Callback when user confirms */
  onConfirm: () => void | Promise<void>;
  /** Callback when user cancels */
  onCancel: () => void;
}

/**
 * Destructive action item for the list
 */
function DestructiveActionItem({ action }: { action: ProposedAction }) {
  const { payload } = action;

  // Type guard check for timeline event payload structure
  if (!('entity_type_name' in payload)) {
    return null;
  }

  if (isObjectDeletedPayload(payload)) {
    // getObjectDeletedTitle always returns a "Delete ..." string for a
    // payload gated by isObjectDeletedPayload above, so stripping the
    // literal "Delete " prefix here is safe.
    const fullTitle = getObjectDeletedTitle(payload, action);
    const detail = fullTitle.replace(/^Delete\s*/, '');
    return (
      <li className='flex items-center gap-2 text-sm text-fg-secondary'>
        <Trash03
          className='w-4 h-4 text-error-600 flex-shrink-0'
          aria-hidden='true'
        />
        <span>
          <span className='font-medium text-error-700'>Delete</span>{' '}
          <span className='text-fg-secondary'>{detail}</span>
        </span>
      </li>
    );
  }

  if (isObjectMergedPayload(payload)) {
    // getObjectMergedTitle always returns a "Merge ..." string for a
    // payload gated by isObjectMergedPayload above, so stripping the
    // literal "Merge " prefix here is safe.
    const fullTitle = getObjectMergedTitle(payload, action);
    const detail = fullTitle.replace(/^Merge\s*/, '');
    return (
      <li className='flex items-center gap-2 text-sm text-fg-secondary'>
        <GitMerge
          className='w-4 h-4 text-warning-600 flex-shrink-0'
          aria-hidden='true'
        />
        <span>
          <span className='font-medium text-warning-700'>Merge</span>{' '}
          <span className='text-fg-secondary'>{detail}</span>
        </span>
      </li>
    );
  }

  return null;
}

/**
 * Modal component that warns users before approving proposals with destructive actions.
 * Requires the user to check an acknowledgment checkbox before confirming.
 */
export function DestructiveWarningModal({
  isOpen,
  destructiveActions,
  isLoading = false,
  onConfirm,
  onCancel,
}: DestructiveWarningModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset acknowledgment when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm();
    } catch (error) {
      console.error('Error in onConfirm:', error);
      throw error;
    }
  }, [onConfirm]);

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title='This action contains destructive changes'
      description='These changes cannot be undone. Please review carefully before proceeding.'
      variant='destructive'
      confirmLabel='Approve anyway'
      isConfirmDisabled={!acknowledged || isLoading}
      isLoading={isLoading}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <div className='p-4 bg-error-50 border border-error-200 rounded-lg'>
        <div className='text-xs font-medium uppercase tracking-wider text-error-700 mb-3'>
          Actions that will be performed:
        </div>
        <ul className='space-y-2'>
          {destructiveActions.map(action => (
            <DestructiveActionItem key={action.id} action={action} />
          ))}
        </ul>
      </div>
      <div className='mt-4'>
        <label className='flex items-start gap-3 cursor-pointer'>
          <Checkbox
            isSelected={acknowledged}
            onChange={setAcknowledged}
            isDisabled={isLoading}
            aria-label='I understand these changes cannot be undone'
          />
          <span className='text-sm text-fg-secondary select-none'>
            I understand these changes cannot be undone
          </span>
        </label>
      </div>
    </ConfirmationDialog>
  );
}
